import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../../components/feature/Navbar';
import { Footer } from '../../../components/feature/Footer';

const BellaVistaDemo: React.FC = () => {
  useEffect(() => {
    document.title = 'Bella Vista Restaurant - Demo | BusinessWebsitePro';
  }, []);

  const menuItems = [
    { name: 'Grilled Salmon', price: '$28', category: 'Main Course', description: 'Fresh Atlantic salmon with lemon butter sauce' },
    { name: 'Ribeye Steak', price: '$42', category: 'Main Course', description: 'Premium 12oz ribeye with garlic mashed potatoes' },
    { name: 'Pasta Carbonara', price: '$22', category: 'Pasta', description: 'Classic Italian carbonara with pancetta' },
    { name: 'Caesar Salad', price: '$14', category: 'Appetizer', description: 'Crisp romaine with house-made dressing' },
    { name: 'Tiramisu', price: '$12', category: 'Dessert', description: 'Traditional Italian coffee-flavored dessert' },
    { name: 'Chocolate Lava Cake', price: '$14', category: 'Dessert', description: 'Warm chocolate cake with vanilla ice cream' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors">
            <i className="ri-arrow-left-line"></i>
            Back to Portfolio
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-orange-100 text-orange-700 text-sm font-semibold px-4 py-2 rounded-full mb-6">
                Restaurant Demo
              </span>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">Bella Vista Restaurant</h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Experience fine dining at its best. Our elegant restaurant website showcases the menu, allows online reservations, and creates an inviting atmosphere for potential diners.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="bg-orange-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap">
                  Make a Reservation
                </button>
                <button className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold border-2 border-orange-600 hover:bg-orange-50 transition-all duration-300 whitespace-nowrap">
                  View Menu
                </button>
              </div>
            </div>
            <div className="w-full h-96 rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/2544c7b33cedc2455c6db43433a10ffa.jpg" 
                alt="Bella Vista Restaurant Website" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Menu</h2>
            <p className="text-xl text-gray-600">Crafted with passion, served with excellence</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {menuItems.map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
                <span className="inline-block bg-orange-100 text-orange-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                  {item.category}
                </span>
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-bold text-gray-900">{item.name}</h3>
                  <span className="text-2xl font-bold text-orange-600">{item.price}</span>
                </div>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Website Features</h2>
            <p className="text-xl text-gray-600">Everything a modern restaurant needs</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-check-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Online Reservations</h3>
              <p className="text-gray-600">Easy booking system for customers to reserve tables 24/7</p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-restaurant-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Digital Menu</h3>
              <p className="text-gray-600">Beautiful menu showcase with photos and descriptions</p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Location & Hours</h3>
              <p className="text-gray-600">Interactive map and business hours information</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Want a Website Like This?</h2>
          <p className="text-xl text-gray-600 mb-8">
            We can create a stunning website for your restaurant that attracts more customers and increases reservations.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-[#14B8A6] text-white px-10 py-5 rounded-xl font-semibold hover:bg-[#0d9488] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            Get Your Website Today
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BellaVistaDemo;
